import { Controller } from "@hotwired/stimulus"

// The events index view toggle's two states, applied to whichever tab is
// current. Kept here rather than in the view because only this controller
// knows which mode is showing. Change them with `events/_view_header`.
const ACTIVE_TAB_CLASSES = ["btn-neutral", "!bg-white", "!text-black"]
const INACTIVE_TAB_CLASSES = ["btn-ghost", "text-gray-500"]

/**
 * Switches the kiosk between Scan and Search.
 *
 * The two modes are both in the DOM and toggled here rather than being two
 * URLs. A Turbo navigation between them would tear down and re-mount the
 * camera on every switch; the kiosk avoids that everywhere else for the same
 * reason (it deliberately does not subscribe to lifecycle broadcasts, because
 * a refresh would stop the scanner mid-service).
 *
 * The camera is not driven from here directly. Switching dispatches
 * `kiosk-mode:scan` / `kiosk-mode:search` on the window, and the scan panel
 * binds those to `qr-scanner#start` / `#stop` — so this controller never needs
 * a handle on the scanner, and the scanner keeps owning its own lifecycle.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["scanPanel", "searchPanel", "scanTab", "searchTab"]
  static values = { mode: { type: String, default: "scan" } }

  declare readonly scanPanelTarget: HTMLElement
  declare readonly searchPanelTarget: HTMLElement
  declare readonly scanTabTarget: HTMLButtonElement
  declare readonly searchTabTarget: HTMLButtonElement
  declare modeValue: string

  connect() {
    this.render()
  }

  showScan() {
    this.modeValue = "scan"
  }

  /**
   * Focus moves here and nowhere else. `modeValueChanged` also fires during
   * initialization, so focusing from `render()` would grab the caret on any
   * page arriving at `?mode=search` — taking the keyboard from someone who
   * never asked for it.
   */
  showSearch() {
    this.modeValue = "search"
    this.searchPanelTarget.querySelector<HTMLInputElement>("input[type=text]")?.focus()
  }

  modeValueChanged() {
    this.render()
  }

  private render() {
    const scanning = this.modeValue !== "search"

    this.scanPanelTarget.classList.toggle("hidden", !scanning)
    this.searchPanelTarget.classList.toggle("hidden", scanning)

    this.setTab(this.scanTabTarget, scanning)
    this.setTab(this.searchTabTarget, !scanning)

    // Let the scan panel start or stop its own camera.
    window.dispatchEvent(new CustomEvent(scanning ? "kiosk-mode:scan" : "kiosk-mode:search"))
  }

  private setTab(tab: HTMLButtonElement, active: boolean) {
    ACTIVE_TAB_CLASSES.forEach((klass) => tab.classList.toggle(klass, active))
    INACTIVE_TAB_CLASSES.forEach((klass) => tab.classList.toggle(klass, !active))
    tab.setAttribute("aria-pressed", String(active))
  }
}
